import React, { forwardRef, FunctionComponent, useCallback, useImperativeHandle, useMemo } from 'react';
import Button from 'components/Button';
import Icon, { IconsType } from 'components/Icon';
import ControlledModal from 'components/Modal/Modal';
import Typography from 'components/Typography';
import { Trans, useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { isMobile } from 'helpers/device_helper';
import { PropChallenge, TradingPlatformAccountInfo } from 'services/generatedClientFromSwagger';
import { Pages } from 'interfaces/main';
import { useHistory } from 'react-router-dom';
import { useKeepModalStyles, useWtBannerStyles } from './KeeperAlertModal.styles';
import {
    DONT_LET_YOUR_CHALLENGE_END,
    KeepChallengePopupState,
    KeepModalType,
} from 'features/prop/store/plans/interfaces';
import {
    prop__plans_closeKeepAlertPopup,
    prop__plans_openKeepAlertPopup,
    prop__changeSelectedTpChallenge,
    prop__plans_setKeeperModalOpened,
} from 'features/prop/store/plans/actions';
import {
    prop_myPlans_getMainAccountTp,
    prop_myPlans_getSelectedChallenge,
    prop_myPlans_keeperModalsOpenedState,
    prop_myPlans_openKeepAlertPopupState,
} from 'features/prop/store/plans/selectors';
import { useStorageState } from 'hooks/useStorageState';
import { challengeKeeperKey } from 'utils/keeper.utils';
import { useSelector, useDispatch } from 'react-redux';
import { mixPanelEventsTypes } from 'helpers/mixPanel';
import analyticsManager from 'helpers/analyticsManager';
import { useMixpanelData } from 'features/prop/hooks/useKeeperMixpanelData';

export enum StyleOption {
    KEEP_CHALLENGE_MODAL = 'KEEP_CHALLENGE_MODAL',
    KEEP_CHALLENGE_BANNER = 'KEEP_CHALLENGE_BANNER',
}

export enum KeeperType {
    MAX_DAILY = 'maxDaily',
    MAX_TOTAL = 'maxTotal',
}

interface KeeperChallengeModalProps {
    children?: React.ReactNode;
    handleCloseAction: () => void;
    selectedChallenge?: PropChallenge;
    keeperType?: KeeperType;
    onExited?: () => void;
}
interface KeeperProps {
    handleCloseAction?: () => void;
    selectedTpAccount?: TradingPlatformAccountInfo;
    hideCloseBtn?: boolean;
    selectedChallenge?: PropChallenge;
    styleOptions?: StyleOption;
    keeperType?: KeeperType;
}

const mapStateToProps = (state) => ({
    selectedTpAccount: prop_myPlans_getMainAccountTp(state),
    selectedChallenge: prop_myPlans_getSelectedChallenge(state),
});

const mapDispatchToProps = {
    handleCloseAction: prop__plans_closeKeepAlertPopup,
    prop__plans_openKeepAlertPopup,
};
const mapDispatchToProps2 = {};

export type KeeperChallengeModalHandle = {
    handleCloseModal: () => void;
};

const KeeperModalTypeCom: FunctionComponent<KeeperProps> = ({
    handleCloseAction,
    selectedTpAccount,
    selectedChallenge,
    styleOptions,
    keeperType,
}) => {
    const modalClasses = useKeepModalStyles();
    const wtClasses = useWtBannerStyles();

    const isModal = styleOptions === StyleOption.KEEP_CHALLENGE_MODAL;
    const classes = isModal ? modalClasses : wtClasses;
    const history = useHistory();
    const { t } = useTranslation('prop');

    const mixPanelData = useMixpanelData(keeperType, selectedChallenge);

    const handleClose = useCallback(() => {
        handleCloseAction?.();
    }, [handleCloseAction]);

    const handleCloseAndMixpanel = useCallback(() => {
        if (isModal) {
            analyticsManager.trackEvent(mixPanelEventsTypes.KEEPER_ELIGIBLE_MODAL_NOTNOW_BTN, mixPanelData);
        } else {
            analyticsManager.trackEvent(mixPanelEventsTypes.KEEPER_TOAST_CLOSE, mixPanelData);
        }
        handleClose();
    }, [handleClose, mixPanelData, isModal]);

    const handleRedirectToKeeperCheckout = useCallback(() => {
        if (isModal) {
            analyticsManager.trackEvent(mixPanelEventsTypes.KEEPER_ELIGIBLE_MODAL_EXTEND_BTN, mixPanelData);
        } else {
            analyticsManager.trackEvent(mixPanelEventsTypes.KEEPER_TOAST_EXTENDMYLIMIT, mixPanelData);
        }
        const newUrl = `/${Pages.PROP}/${Pages.PROP__CHECKOUT}?planId=${selectedChallenge?.plan}&challengeId=${selectedChallenge?.id}&keeper=true`;
        history.push(newUrl);
        handleClose();
    }, [handleClose, history, selectedChallenge?.id, selectedChallenge?.plan, mixPanelData, isModal]);

    const {
        onClick,
        second_btn_action,
        translationParams,
        iconType,
        second_btn_variant,
        second_btn_color,
        btn_variant,
        btn_color,
        btn_container,
        hideCloseBtn,
    } = useMemo(() => {
        return {
            iconType: IconsType.challengeEndOne,
            onClick: handleRedirectToKeeperCheckout,
            second_btn_action: !isModal ? undefined : handleCloseAndMixpanel,
            second_btn_variant: !isModal ? undefined : 'text',
            second_btn_color: !isModal ? undefined : 'default',
            btn_variant: 'contained',
            btn_color: 'primary',
            btn_container: '',
            translationParams: {
                chip: { challengeId: selectedChallenge?.tpName },
                first_subtitle: {
                    originalMaxDailyLoss:
                        keeperType === KeeperType.MAX_TOTAL
                            ? Math.abs(selectedChallenge?.shutDownPrecentage ?? 0)
                            : Math.abs(selectedChallenge?.maxDailyLossPrecentage ?? 0),
                    conditionName:
                        keeperType === KeeperType.MAX_TOTAL
                            ? t('prop:keep_modal.condition.max_total')
                            : t('prop:keep_modal.condition.max_daily'),
                },
                second_subtitle: (
                    <Trans
                        i18nKey="prop:keep_modal.dont_let_your_challenge_end.second_subtitle"
                        components={{
                            c: <span className={classes.challengeKeeper} />,
                        }}
                        values={{
                            maxDailyLoss:
                                keeperType === KeeperType.MAX_TOTAL
                                    ? Math.abs(selectedChallenge?.keeper?.maxTotal?.conditionValue ?? 0)
                                    : Math.abs(selectedChallenge?.keeper?.maxDaily?.conditionValue ?? 0),
                            pricePurchaseKeeper:
                                keeperType === KeeperType.MAX_TOTAL
                                    ? selectedChallenge?.keeper?.maxTotal?.priceValue
                                    : selectedChallenge?.keeper?.maxDaily?.priceValue,
                            currency: selectedTpAccount?.baseCurrency?.symbol,
                        }}
                    />
                ),
                banner_content: (
                    <Trans
                        i18nKey="prop:keep_modal.dont_let_your_challenge_end.banner_content"
                        components={{
                            c: <span className={classes.challengeKeeper} />,
                        }}
                        values={{
                            originalMaxDailyLoss:
                                keeperType === KeeperType.MAX_TOTAL
                                    ? Math.abs(selectedChallenge?.shutDownPrecentage ?? 0)
                                    : Math.abs(selectedChallenge?.maxDailyLossPrecentage ?? 0),
                            maxDailyLoss:
                                keeperType === KeeperType.MAX_TOTAL
                                    ? Math.abs(selectedChallenge?.keeper?.maxTotal?.conditionValue ?? 0)
                                    : Math.abs(selectedChallenge?.keeper?.maxDaily?.conditionValue ?? 0),
                            pricePurchaseKeeper:
                                keeperType === KeeperType.MAX_TOTAL
                                    ? selectedChallenge?.keeper?.maxTotal?.priceValue
                                    : selectedChallenge?.keeper?.maxDaily?.priceValue,
                            currency: selectedTpAccount?.baseCurrency?.symbol,
                        }}
                    />
                ),
            },
            hideCloseBtn: false,
        };
    }, [
        keeperType,
        handleRedirectToKeeperCheckout,
        isModal,
        selectedChallenge?.tpName,
        selectedChallenge?.shutDownPrecentage,
        selectedChallenge?.maxDailyLossPrecentage,
        selectedChallenge?.keeper?.maxTotal?.conditionValue,
        selectedChallenge?.keeper?.maxTotal?.priceValue,
        selectedChallenge?.keeper?.maxDaily?.conditionValue,
        selectedChallenge?.keeper?.maxDaily?.priceValue,
        t,
        classes.challengeKeeper,
        selectedTpAccount?.baseCurrency?.symbol,
        handleCloseAndMixpanel,
    ]);

    const modalContent: {
        title: string;
        first_subtitle?: string | any;
        second_subtitle?: any;
        chip?: string;
        btn: string;
        second_btn?: string;
        iconType?: IconsType;
        action: any;
        second_btn_action?: any;
        second_btn_variant?: string;
        second_btn_color?: string;
        btn_variant?: string;
        btn_color?: string;
        btn_container?: string;
        hideCloseBtn?: boolean;
        banner_content?: any;
    } = useMemo(() => {
        const defaultChipTrans: {
            challengeId?: string | null | undefined;
        } = translationParams?.chip || {};
        const type = DONT_LET_YOUR_CHALLENGE_END;
        return {
            title: t(`keep_modal.${type}.title`),
            first_subtitle:
                styleOptions === StyleOption.KEEP_CHALLENGE_BANNER
                    ? translationParams?.banner_content
                    : t(`keep_modal.${type}.first_subtitle`, translationParams?.first_subtitle),
            second_subtitle: !isModal
                ? undefined
                : typeof translationParams?.second_subtitle === 'string'
                  ? t(`keep_modal.${type}.second_subtitle`, { second_subtitle: translationParams?.second_subtitle })
                  : translationParams?.second_subtitle,
            btn: t(`keep_modal.${type}.btn`),
            second_btn: !isModal ? undefined : t(`keep_modal.${type}.second_btn`),
            btn_container,
            second_btn_variant,
            second_btn_color,
            btn_variant,
            btn_color,
            iconType: iconType,
            second_btn_action: second_btn_action,
            action: onClick,
            chip: t(`keep_modal.${type}.chip`, defaultChipTrans),
            hideCloseBtn,
        };
    }, [
        translationParams?.chip,
        translationParams?.first_subtitle,
        translationParams?.second_subtitle,
        translationParams?.banner_content,
        t,
        isModal,
        btn_container,
        second_btn_variant,
        second_btn_color,
        btn_variant,
        btn_color,
        iconType,
        second_btn_action,
        onClick,
        hideCloseBtn,
        styleOptions,
    ]);

    return (
        <div className={classes.modal_container}>
            <div className={classes.modal_button}>
                {!modalContent.hideCloseBtn && (
                    <Button onClick={handleCloseAndMixpanel} className={classes.closeIcon}>
                        <Icon name={IconsType.closeKeeper} />
                    </Button>
                )}
            </div>
            <div
                className={`${classes.modal_content} ${
                    modalContent?.btn || modalContent?.second_btn
                        ? isMobile()
                            ? classes.marginBottomMobile
                            : classes.marginBottomDefault
                        : classes.marginBottomNone
                }`}
            >
                <div className={isModal ? '' : classes.modalContent}>
                    <div className={classes.topIcon}>
                        <Icon name={modalContent?.iconType!} className={!isModal ? classes.iconSize : ''} />
                    </div>
                    <Typography
                        variant={styleOptions === StyleOption.KEEP_CHALLENGE_MODAL ? 'dXsSemibold' : 'tMdSemibold'}
                        className={classes.title}
                    >
                        {modalContent.title}
                        {modalContent.chip && !isModal && (
                            <div className={classes.chip}>
                                <Typography variant="tXsRegular">{modalContent.chip}</Typography>
                            </div>
                        )}
                    </Typography>
                </div>
                <div className={isModal ? classes.modalContentTxtBlock : classes.bannerContentTxtBlock}>
                    <Typography variant="tSmallRegular" className={classes.first_subtitle}>
                        {modalContent.first_subtitle}
                    </Typography>
                    {modalContent.second_subtitle && (
                        <Typography variant="tSmallRegular" className={classes.first_subtitle}>
                            {modalContent.second_subtitle}
                        </Typography>
                    )}
                    {modalContent.chip && isModal && (
                        <div className={classes.chip}>
                            <Typography className={classes.typoChip} variant="tXsRegular">
                                {modalContent.chip}
                            </Typography>
                        </div>
                    )}
                </div>
            </div>

            {(modalContent?.btn || modalContent?.second_btn) && (
                <div className={`${classes.buttons} ${modalContent.btn_container}`}>
                    {modalContent?.btn && (
                        <Button
                            variant={modalContent?.btn_variant as any}
                            color={modalContent?.btn_color as any}
                            onClick={modalContent.action}
                            className={classes.closeBtnModal}
                        >
                            {modalContent.btn}
                        </Button>
                    )}
                    {modalContent?.second_btn && (
                        <Button
                            variant={modalContent?.second_btn_variant as any}
                            color={modalContent?.second_btn_color as any}
                            onClick={modalContent.second_btn_action}
                            className={classes.keepBtnModal}
                        >
                            {modalContent.second_btn}
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
};

export const KeeperAlertContent = connect(mapStateToProps, mapDispatchToProps2)(KeeperModalTypeCom);

const KeeperAlertModal = forwardRef<KeeperChallengeModalHandle, KeeperChallengeModalProps>((props, ref) => {
    const { handleCloseAction, selectedChallenge, keeperType, onExited } = props;

    const keeperModalsOpenedState = useSelector(prop_myPlans_keeperModalsOpenedState)[selectedChallenge?.id];
    const dispatch = useDispatch();

    const modalStorageKey = `${challengeKeeperKey}-${keeperType}-${selectedChallenge?.id}`;

    const [displayCount, setDisplayCount] = useStorageState<number>(
        modalStorageKey,
        parseInt(localStorage.getItem(modalStorageKey) || '0', 10),
        'localStorage'
    );

    const handleCloseModal = useCallback(() => {
        if (keeperType) {
            dispatch(prop__plans_setKeeperModalOpened(keeperType));
        }

        setDisplayCount((prev) => {
            const next = prev + 1;
            localStorage.setItem(modalStorageKey, next.toString());
            return next;
        });
        handleCloseAction();
        setTimeout(() => onExited?.(), 1500);
    }, [keeperType, setDisplayCount, handleCloseAction, onExited, dispatch, modalStorageKey]);

    useImperativeHandle(ref, () => ({
        handleCloseModal,
    }));

    const BackdropProps = useMemo(() => {
        if (keeperType === KeeperType.MAX_DAILY || keeperType === KeeperType.MAX_TOTAL) {
            return { style: { backdropFilter: 'blur(8px)' } };
        }
        return { style: { display: 'none' } };
    }, [keeperType]);

    const isModalOpen = useMemo(
        () => displayCount < 2 && Boolean(keeperType && !keeperModalsOpenedState?.[keeperType]),
        [displayCount, keeperModalsOpenedState, keeperType]
    );
    return (
        <ControlledModal
            open={isModalOpen}
            handleClose={handleCloseModal}
            BackdropProps={BackdropProps}
            paperWidth={isMobile() ? `calc(100% - 32px)` : '400px'}
            keeperState="alert"
        >
            <KeeperAlertContent
                styleOptions={StyleOption.KEEP_CHALLENGE_MODAL}
                handleCloseAction={handleCloseModal}
                skipIncrementCounter={true}
                keeperType={keeperType}
            />
        </ControlledModal>
    );
});

export default connect(mapStateToProps, mapDispatchToProps)(React.memo(KeeperAlertModal));
